import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, splitBySeverity } from '../api';
import type {
  Account,
  BatchAttachment,
  BatchTotals,
  OcrClassifyResponse,
  ParsedWorkbookResponse,
  Party,
  RateDriftItem,
  RawUplineRow,
  Settlement,
  SettlementAdvisory,
  VarianceSeverity,
  VerifyBlocker,
  WeeklyBatch,
} from '../api';
import { BlockerChip, sourceIdOf, targetKindOf } from '../batch/blockerChips';
import { VerifyConfirmationModal } from '../batch/VerifyConfirmationModal';
import { Upload } from 'lucide-react';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { ActionQueueCard, MetricStrip, StickySummaryBar } from '../ui/mobileOps';
import { Btn, SectionCard, StatePill } from '../ui/components';
import {
  Button,
  ErrorBanner,
  Spinner,
} from '../ui/primitives';
import { UnmatchedPanel } from '../batch/UnmatchedPanel';
import { ReopenFixWizard, formatRatePercent } from '../batch/ReopenFixWizard';
import { SettlementRow } from '../batch/SettlementRow';
import { ImportDialog } from '../import/ImportDialog';
import { OcrAliasStep } from '../import/OcrAliasStep';
import { ManualEntryGrid } from '../import/ManualEntryGrid';
import { Modal } from '../ui/Modal';
import { usePinGate } from '../auth/pinGate';

export function BatchDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const batchId = Number(idParam);
  const nav = useNavigate();
  const qc = useQueryClient();
  const readOnly = useReadOnly();

  const batch = useQuery<WeeklyBatch>({
    queryKey: ['batch', batchId],
    queryFn: () => api.get<WeeklyBatch>(`/api/batches/${batchId}`),
    enabled: Number.isFinite(batchId),
  });
  const attachments = useQuery<BatchAttachment[]>({
    queryKey: ['batch', batchId, 'attachments'],
    queryFn: () => api.get<BatchAttachment[]>(`/api/batches/${batchId}/attachments`),
    enabled: Number.isFinite(batchId),
  });
  const rawRows = useQuery<RawUplineRow[]>({
    queryKey: ['batch', batchId, 'raw-rows'],
    queryFn: () => api.get<RawUplineRow[]>(`/api/batches/${batchId}/raw-rows`),
    enabled: Number.isFinite(batchId),
  });
  const settlements = useQuery<Settlement[]>({
    queryKey: ['batch', batchId, 'settlements'],
    queryFn: () => api.get<Settlement[]>(`/api/batches/${batchId}/settlements`),
    enabled: Number.isFinite(batchId),
  });
  const totals = useQuery<BatchTotals>({
    queryKey: ['batch', batchId, 'totals'],
    queryFn: () => api.get<BatchTotals>(`/api/batches/${batchId}/totals`),
    enabled: Number.isFinite(batchId),
  });
  const advisories = useQuery<SettlementAdvisory[]>({
    queryKey: ['batch', batchId, 'advisories'],
    queryFn: () => api.get<SettlementAdvisory[]>(`/api/batches/${batchId}/advisories`),
    enabled: Number.isFinite(batchId),
  });
  const blockers = useQuery<VerifyBlocker[]>({
    queryKey: ['batch', batchId, 'blockers'],
    queryFn: () => api.get<VerifyBlocker[]>(`/api/batches/${batchId}/blockers`),
    enabled: Number.isFinite(batchId),
  });
  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });
  const clientsQ = useQuery<Party[]>({
    queryKey: ['parties', 'client'],
    queryFn: () => api.get<Party[]>('/api/parties?role=client'),
  });
  const uplinesQ = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });
  const features = useQuery<{ ocrEnabled: boolean }>({
    queryKey: ['settings', 'features'],
    queryFn: () => api.get<{ ocrEnabled: boolean }>('/api/settings/features'),
    staleTime: 60_000,
  });

  const [importOpen, setImportOpen] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState<'' | VarianceSeverity>('');
  const [filterClient, setFilterClient] = useState<string>('');
  const [genericError, setGenericError] = useState<string | null>(null);
  const [ocrPreseeded, setOcrPreseeded] = useState<ParsedWorkbookResponse | null>(null);
  const [ocrAliasData, setOcrAliasData] = useState<OcrClassifyResponse | null>(null);
  const [allowedAccountNames, setAllowedAccountNames] = useState<Set<string> | undefined>();
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  // Reopen & Fix wizard — `?fix=1` (from the rate-drift chips) auto-opens it.
  const [searchParams] = useSearchParams();
  const [fixWizardOpen, setFixWizardOpen] = useState(
    searchParams.get('fix') === '1',
  );
  const { withPin } = usePinGate();

  // Rate-drift detector (Phase 3c) — locked settlements whose snapshotted
  // rate no longer matches the assignment covering this week. Detect only.
  const rateDriftQ = useQuery<RateDriftItem[]>({
    queryKey: ['rate-drift'],
    queryFn: () => api.get<RateDriftItem[]>('/api/rate-drift'),
    staleTime: 60_000,
  });
  const driftHere = (rateDriftQ.data ?? []).filter((d) => d.batchId === batchId);

  // Click-to-jump for verify-readiness chips. Scrolls the target row into
  // view and pulses it via the `.flash-highlight` class (defined in styles.css).
  const scrollToRow = useCallback(
    (kind: 'raw-row' | 'settlement', id: number) => {
      const selector = `[data-row-kind="${kind}"][data-row-id="${id}"]`;
      const nodes = document.querySelectorAll<HTMLElement>(selector);
      // Multiple nodes possible (desktop table + mobile card both render). Pick
      // the one that's currently visible (offsetParent is null for hidden ones).
      const target = Array.from(nodes).find((n) => n.offsetParent !== null) ?? nodes[0];
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.remove('flash-highlight');
      // Reflow so re-adding the class restarts the animation.
      void target.offsetWidth;
      target.classList.add('flash-highlight');
      window.setTimeout(() => target.classList.remove('flash-highlight'), 2200);
    },
    [],
  );

  const ocrClassifyMut = useMutation({
    mutationFn: (attachmentId: number) =>
      api.post<OcrClassifyResponse>(
        `/api/batches/${batchId}/attachments/${attachmentId}/ocr-classify`,
      ),
    onSuccess: (data) => {
      setGenericError(null);
      if (data.fallback) {
        setGenericError(
          'Vision AI failed to parse structured data — fell back to basic OCR. ' +
          'Results may be less accurate. If this looks wrong, use manual entry instead.',
        );
      }
      setOcrAliasData(data);
    },
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });

  const computeMut = useMutation({
    mutationFn: () => api.post<unknown>(`/api/batches/${batchId}/compute`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });

  const verifyMut = useMutation({
    mutationFn: (acknowledgedWarnings?: VerifyBlocker[]) =>
      api.post<WeeklyBatch>(
        `/api/batches/${batchId}/verify`,
        acknowledgedWarnings ? { acknowledgedWarnings } : undefined,
      ),
    onSuccess: () => {
      setVerifyModalOpen(false);
      void qc.invalidateQueries({ queryKey: ['batch', batchId] });
    },
    onError: (e) => {
      // 412 = the server's current warnings don't match what we acknowledged.
      // Refetch and let the user re-confirm; do not auto-retry.
      if (e instanceof ApiError && e.status === 412) {
        setVerifyModalOpen(false);
        setGenericError(
          'Warnings changed since you opened this page. Review the panel and try again.',
        );
        void qc.invalidateQueries({ queryKey: ['batch', batchId, 'blockers'] });
        return;
      }
      setGenericError(e instanceof ApiError ? e.message : String(e));
    },
  });
  const unlockMut = useMutation({
    mutationFn: () => api.post<WeeklyBatch>(`/api/batches/${batchId}/unlock`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const postMut = useMutation({
    mutationFn: () =>
      api.post<{ batch: WeeklyBatch; entriesCreated: number; settlementsLocked: number }>(
        `/api/batches/${batchId}/post`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const unpostMut = useMutation({
    mutationFn: (reason: string) =>
      api.post<{ batch: WeeklyBatch; entriesReversed: number; settlementsUnlocked: number }>(
        `/api/batches/${batchId}/unpost`,
        { reason },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/api/batches/${batchId}`),
    onSuccess: () => nav('/batches'),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });

  const accountsById = useMemo(
    () => new Map((accountsQ.data ?? []).map((a) => [a.id, a])),
    [accountsQ.data],
  );
  const clientsById = useMemo(
    () => new Map((clientsQ.data ?? []).map((c) => [c.id, c])),
    [clientsQ.data],
  );

  if (!Number.isFinite(batchId)) {
    return (
      <AppShell
        title="Bad batch id"
        toolbar={
          <PageHeader
            section="batches"
            title="Bad batch id"
            breadcrumb={[{ label: 'Batches', to: '/batches' }]}
          />
        }
      >
        <SectionCard section="batches">
          <ErrorBanner>Not a valid batch id.</ErrorBanner>
        </SectionCard>
      </AppShell>
    );
  }

  if (batch.isLoading) {
    return (
      <AppShell
        title="Loading…"
        toolbar={
          <PageHeader
            section="batches"
            title="Loading…"
            breadcrumb={[{ label: 'Batches', to: '/batches' }]}
          />
        }
      >
        <div className="flex items-center gap-2 text-small text-ink-muted">
          <Spinner /> loading…
        </div>
      </AppShell>
    );
  }

  if (!batch.data) {
    return (
      <AppShell
        title="Not found"
        toolbar={
          <PageHeader
            section="batches"
            title="Batch not found"
            breadcrumb={[{ label: 'Batches', to: '/batches' }]}
          />
        }
      >
        <SectionCard section="batches">
          <ErrorBanner>Batch {batchId} not found.</ErrorBanner>
        </SectionCard>
      </AppShell>
    );
  }

  const uplineName =
    uplinesQ.data?.find((p) => p.id === batch.data.uplinePartyId)?.displayName ??
    `#${batch.data.uplinePartyId}`;
  const editable = batch.data.state !== 'archived';

  // Split verify-readiness entries into hard blocks and soft warnings. The
  // Verify button is disabled iff `blocks.length > 0`; warnings require an
  // acknowledgement modal but don't bar the transition.
  const { blocks, warns } = splitBySeverity(blockers.data ?? []);

  // Group settlements by client party (mirrors Google Sheet layout).
  const filtered = (settlements.data ?? []).filter((s) => {
    if (filterSeverity && s.varianceSeverity !== filterSeverity) return false;
    if (filterClient) {
      if (filterClient === 'self' && s.clientPartyId !== null) return false;
      if (filterClient !== 'self' && String(s.clientPartyId ?? '') !== filterClient) return false;
    }
    return true;
  });
  // Key: clientPartyId (null → 'self')
  const byClient = new Map<string, Settlement[]>();
  for (const s of filtered) {
    const key = s.clientPartyId !== null ? String(s.clientPartyId) : 'self';
    if (!byClient.has(key)) byClient.set(key, []);
    byClient.get(key)!.push(s);
  }
  for (const list of byClient.values()) {
    list.sort((a, b) => {
      const an = accountsById.get(a.accountId)?.canonicalName ?? '';
      const bn = accountsById.get(b.accountId)?.canonicalName ?? '';
      return an.localeCompare(bn);
    });
  }
  // Sort client groups: named clients alphabetically, 'self' last
  const clientGroupOrder = Array.from(byClient.keys()).sort((a, b) => {
    if (a === 'self') return 1;
    if (b === 'self') return -1;
    const na = clientsById.get(Number(a))?.displayName ?? '';
    const nb = clientsById.get(Number(b))?.displayName ?? '';
    return na.localeCompare(nb);
  });

  const pageTitle = `Batch #${batch.data.id} — ${uplineName} — ${batch.data.weekMonday}`;
  const attachmentCount = attachments.data?.length ?? 0;
  const unmatchedRawCount = (rawRows.data ?? []).filter(
    (row) => row.matchedAccountId === null && !row.ignored,
  ).length;
  const mobileDeskTitle =
    batch.data.state === 'draft'
      ? 'Finish intake before you verify this batch'
      : batch.data.state === 'verified'
      ? 'This batch is ready to post into ledgers'
      : 'This batch is already posted and audit-safe';
  const mobileDeskDescription =
    batch.data.state === 'draft'
      ? 'Rows, notes, and attachments stay together here so you can finish the batch without hunting across the page.'
      : batch.data.state === 'verified'
      ? 'Check the warnings, then post once the totals look right.'
      : 'Use notes and attachments for context. Unpost only when a real correction is required.';  return (
    <AppShell
      title={pageTitle}
      toolbar={
        <PageHeader
          section="batches"
          title={`Batch #${batch.data.id} — ${uplineName}`}
          subtitle={`Week of ${batch.data.weekMonday}`}
          breadcrumb={[{ label: 'Batches', to: '/batches' }]}
          actions={
            editable && !readOnly ? (
              <Btn size="sm" onClick={() => setImportOpen(true)}>
                <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                <span className="hidden sm:inline">Import rows</span>
              </Btn>
            ) : undefined
          }
        />
      }
    >
      {/* Single-column flow: the wide Settlements table gets the FULL content
          width (no longer choked into a 2fr sub-column); the side cards lay
          out as a row below it. */}
      <div className="space-y-4">
        <div className="min-w-0 space-y-4">
          {genericError && <ErrorBanner>{genericError}</ErrorBanner>}

          {/* Rate drift (Phase 3c) — detect only; the fix is Reopen & Fix. */}
          {driftHere.length > 0 && (
            <div className="rounded-card border border-amber-300 bg-amber-50 p-4">
              <h3 className="text-small font-semibold text-amber-700">
                Rate looks off in this posted week
              </h3>
              <ul className="mt-1 space-y-0.5 text-small text-amber-700">
                {driftHere.map((d) => (
                  <li key={d.settlementId}>
                    <strong>{d.accountName}</strong> was posted at{' '}
                    {formatRatePercent(d.uplineRateWas)} upline
                    {d.clientRateWas !== d.clientRateNow &&
                      ` / ${formatRatePercent(d.clientRateWas)} client`}
                    , but the rate on file for this week is now{' '}
                    {formatRatePercent(d.uplineRateNow)}
                    {d.clientRateWas !== d.clientRateNow &&
                      ` / ${formatRatePercent(d.clientRateNow)}`}
                    .
                  </li>
                ))}
              </ul>
              {!readOnly && (
                <div className="mt-2">
                  <Btn size="sm" onClick={() => setFixWizardOpen(true)}>
                    Reopen &amp; fix this week
                  </Btn>
                </div>
              )}
            </div>
          )}

          {/* ── Headline stats ───────────────────────────────────────────── */}
          {totals.data && (
            <>
              <div className="md:hidden">
                <StickySummaryBar
                  section="batches"
                  title={`Batch #${batch.data.id} · ${uplineName}`}
                  subtitle={`Week of ${batch.data.weekMonday}`}
                  metrics={[
                    {
                      label: 'Margin',
                      value: totals.data.userMarginSum,
                      tone: Number(totals.data.userMarginSum) < 0 ? 'neg' : 'pos',
                    },
                    {
                      label: 'Major',
                      value: String(totals.data.majorVarianceCount),
                      tone: totals.data.majorVarianceCount > 0 ? 'warn' : 'default',
                    },
                    {
                      label: 'Pending',
                      value: String(totals.data.pendingCount),
                      tone: totals.data.pendingCount > 0 ? 'warn' : 'default',
                    },
                  ]}
                  actions={
                    !readOnly ? (
                      <>
                        {batch.data.state !== 'archived' && (
                          <Btn size="sm" variant="secondary" onClick={() => computeMut.mutate()} loading={computeMut.isPending}>
                            Compute
                          </Btn>
                        )}
                        {batch.data.state === 'draft' && (
                          <Btn
                            size="sm"
                            onClick={() => {
                              if (warns.length > 0) {
                                setVerifyModalOpen(true);
                              } else {
                                verifyMut.mutate(undefined);
                              }
                            }}
                            loading={verifyMut.isPending && !verifyModalOpen}
                            disabled={blocks.length > 0}
                          >
                            Verify
                          </Btn>
                        )}
                        {batch.data.state === 'verified' && (
                          <Btn
                            size="sm"
                            onClick={() => {
                              const settlementsCount = settlements.data?.length ?? 0;
                              const ok = confirm(
                                `Post this batch to ledgers?\n\n${settlementsCount} settlement(s) will be written as immutable ledger entries across client, upline, and self ledgers. Posted settlements cannot be edited until the batch is unposted.\n\nContinue?`,
                              );
                              if (ok) postMut.mutate();
                            }}
                            loading={postMut.isPending}
                            disabled={blocks.length > 0}
                          >
                            Post
                          </Btn>
                        )}
                      </>
                    ) : undefined
                  }
            />
              </div>
              <MetricStrip
                section="batches"
                className="hidden md:grid"
                items={[
                  {
                    label: 'Your margin',
                    value: totals.data.userMarginSum,
                    note: 'K-INR',
                    tone: Number(totals.data.userMarginSum) < 0 ? 'neg' : 'pos',
                  },
                  {
                    label: 'Major var.',
                    value: String(totals.data.majorVarianceCount),
                    note: 'to review',
                    tone: totals.data.majorVarianceCount > 0 ? 'warn' : 'default',
                  },
                  {
                    label: 'Pending',
                    value: String(totals.data.pendingCount),
                    note: 'rows',
                    tone: totals.data.pendingCount > 0 ? 'warn' : 'default',
                  },
                ]}
              />
            </>
          )}

          <div className="lg:hidden">
            <ActionQueueCard
              section="batches"
              eyebrow="Batch desk"
              title={mobileDeskTitle}
              description={mobileDeskDescription}
              tone={
                blocks.length > 0 || unmatchedRawCount > 0
                  ? 'warn'
                  : batch.data.state === 'archived'
                  ? 'neutral'
                  : 'accent'
              }
              meta={
                <div className="grid grid-cols-2 gap-2 text-[12px] leading-5">
                  <div className="rounded-[16px] bg-surface-raised px-3 py-2 ring-1 ring-border/50">
                    <div className="text-ink-muted">State</div>
                    <div className="mt-0.5 font-medium text-ink">{batch.data.state}</div>
                  </div>
                  <div className="rounded-[16px] bg-surface-raised px-3 py-2 ring-1 ring-border/50">
                    <div className="text-ink-muted">Rows</div>
                    <div className="mt-0.5 font-medium text-ink">{totals.data?.rowCount ?? 0}</div>
                  </div>
                  <div className="rounded-[16px] bg-surface-raised px-3 py-2 ring-1 ring-border/50">
                    <div className="text-ink-muted">Attachments</div>
                    <div className="mt-0.5 font-medium text-ink">{attachmentCount}</div>
                  </div>
                  <div className="rounded-[16px] bg-surface-raised px-3 py-2 ring-1 ring-border/50">
                    <div className="text-ink-muted">Unmatched</div>
                    <div className="mt-0.5 font-medium text-ink">{unmatchedRawCount}</div>
                  </div>
                </div>
              }
              actions={
                !readOnly ? (
                  <>
                    {editable && (
                      <Btn size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
                        Import rows
                      </Btn>
                    )}
                    {editable && (
                      <Btn
                        size="sm"
                        variant="secondary"
                        onClick={() => setManualEntryOpen((open) => !open)}
                      >
                        {manualEntryOpen ? 'Hide manual entry' : 'Manual entry'}
                      </Btn>
                    )}
                  </>
                ) : undefined
              }
            />
          </div>

          {/* Verify-readiness: hard blocks (red) + soft warnings (amber). */}
          {(blocks.length > 0 || warns.length > 0) && (
            <div className="space-y-3">
              {blocks.length > 0 && (
                <div className="rounded-card border border-neg/30 bg-neg/5 p-4">
                  <h3 className="mb-2 text-small font-semibold text-neg">
                    Must fix before verifying ({blocks.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {blocks.map((b, i) => (
                      <BlockerChip
                        key={`block-${i}`}
                        blocker={b}
                        onClick={() => scrollToRow(targetKindOf(b), sourceIdOf(b))}
                      />
                    ))}
                  </div>
                </div>
              )}
              {warns.length > 0 && (
                <div className="rounded-card border border-amber-300 bg-amber-50 p-4">
                  <h3 className="mb-2 text-small font-semibold text-amber-700">
                    Warnings ({warns.length})
                  </h3>
                  <p className="mb-2 text-caption text-amber-700">
                    Verify is allowed; you&apos;ll be asked to confirm each warning.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {warns.map((b, i) => (
                      <BlockerChip
                        key={`warn-${i}`}
                        blocker={b}
                        onClick={() => scrollToRow(targetKindOf(b), sourceIdOf(b))}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <UnmatchedPanel batchId={batchId} />

          <div className="space-y-4 lg:hidden">
            <BatchNotesCard
              batchId={batchId}
              initialNotes={batch.data.notes}
              readOnly={readOnly}
              onError={(msg) => setGenericError(msg)}
            />

            <SectionCard section="batches" title="Attachments and intake">
              <p className="text-small text-ink-muted">
                {attachmentCount} attachment(s) on this batch.
              </p>
              {attachments.data && attachments.data.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {attachments.data.map((a) => (
                    <li key={a.id} className="rounded-[18px] border border-border/70 bg-surface-raised p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-small font-medium text-ink">
                          {a.originalFilename}
                        </span>
                        <span className="shrink-0 text-caption text-ink-muted">
                          {(a.byteSize / 1024).toFixed(0)} kB
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="font-mono text-caption text-ink-muted">
                          {a.sha256.slice(0, 12)}…
                        </span>
                        <a
                          href={`/api/attachments/${a.sha256}${attachmentExt(a.originalFilename)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-caption font-medium text-sec-batches hover:underline"
                        >
                          {isImageMimeType(a.mimeType) ? 'View' : 'Download'}
                        </a>
                      </div>
                      {features.data?.ocrEnabled &&
                        isImageMimeType(a.mimeType) &&
                        editable &&
                        !readOnly && (
                          <div className="mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="px-2 py-1 text-small"
                              onClick={() => {
                                setGenericError(null);
                                ocrClassifyMut.mutate(a.id);
                              }}
                              disabled={ocrClassifyMut.isPending}
                            >
                              {ocrClassifyMut.isPending && ocrClassifyMut.variables === a.id ? (
                                <Spinner />
                              ) : (
                                'Extract from image'
                              )}
                            </Button>
                          </div>
                        )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-caption text-ink-muted">No attachments yet.</p>
              )}

              {!readOnly && (
                <div className="mt-3 border-t border-border pt-3">
                  <AttachmentUploader
                    batchId={batchId}
                    onUploaded={() =>
                      void qc.invalidateQueries({ queryKey: ['batch', batchId, 'attachments'] })
                    }
                    onError={(msg) => setGenericError(msg)}
                  />
                </div>
              )}
            </SectionCard>
          </div>

          {/* ── Settlements ──────────────────────────────────────────────── */}
          <SectionCard section="batches">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-small font-semibold text-ink">Settlements</h3>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Variance filter"
                  className="min-h-[36px] w-full min-w-0 max-w-full rounded-md border border-border bg-surface px-2 py-1 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 sm:w-auto"
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value as '' | VarianceSeverity)}
                >
                  <option value="">All variance</option>
                  <option value="NONE">NONE</option>
                  <option value="MINOR">MINOR</option>
                  <option value="REVIEW">REVIEW</option>
                  <option value="MAJOR">MAJOR</option>
                </select>
                <select
                  aria-label="Client filter"
                  className="min-h-[36px] w-full min-w-0 max-w-full rounded-md border border-border bg-surface px-2 py-1 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 sm:w-auto"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                >
                  <option value="">All clients</option>
                  <option value="self">Self</option>
                  {(clientsQ.data ?? []).map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
                <span className="text-caption text-ink-muted">
                  {filtered.length}/{settlements.data?.length ?? 0} shown
                </span>
              </div>
            </div>

            {/* Desktop: full settlements table */}
            <div className="mt-3 hidden overflow-x-auto md:block">
              <table className="min-w-full text-small">
                <thead className="sticky top-0 z-10 bg-surface-raised text-caption uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Account</th>
                    {/* ── client side ─────────────────────────── */}
                    <th className="border-l border-border/50 px-4 py-2.5 text-right text-sec-batches">Client pts</th>
                    <th className="px-4 py-2.5 text-right text-sec-batches">C.rate</th>
                    <th className="px-4 py-2.5 text-right text-sec-batches">Client amt</th>
                    {/* ── upline side ─────────────────────────── */}
                    <th className="border-l border-border/50 px-4 py-2.5 text-right text-brand/70">Upline pts</th>
                    <th className="px-4 py-2.5 text-right text-brand/70">U.rate</th>
                    <th className="px-4 py-2.5 text-right text-brand/70">Upline cost</th>
                    {/* ── result ──────────────────────────────── */}
                    <th className="border-l border-border/50 px-4 py-2.5 text-right">Margin</th>
                    <th className="px-4 py-2.5 text-center">Var.</th>
                    <th className="px-4 py-2.5 text-left">Flags</th>
                    {/* Actions pinned to the right edge so Edit stays visible
                        even when the wide table needs horizontal scroll. */}
                    <th className="sticky right-0 bg-surface-raised px-4 py-2.5 text-right shadow-[-10px_0_12px_-10px_rgba(15,23,42,0.18)]">
                      Edit
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {clientGroupOrder.map((clientKey) => (
                    <ClientGroupRows
                      key={clientKey}
                      clientKey={clientKey}
                      rows={byClient.get(clientKey)!}
                      accountsById={accountsById}
                      clientsById={clientsById}
                      advisories={advisories.data ?? []}
                      editable={editable && !readOnly}
                      onEdited={() => {
                        void qc.invalidateQueries({ queryKey: ['batch', batchId] });
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: settlement cards grouped by client */}
            <div className="mt-3 space-y-4 md:hidden">
              {clientGroupOrder.map((clientKey) => {
                const clientName =
                  clientKey === 'self'
                    ? 'Self (company)'
                    : (clientsById.get(Number(clientKey))?.displayName ?? `Client #${clientKey}`);
                const groupRows = byClient.get(clientKey)!;
                return (
                  <div key={clientKey}>
                    <div className="mb-2 px-1 text-caption font-semibold uppercase tracking-wide text-sec-batches">
                      {clientName}
                      <span className="ml-1.5 font-normal normal-case tracking-normal text-sec-batches opacity-70">
                        ({groupRows.length} account{groupRows.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="space-y-3">
                      {groupRows.map((s) => (
                        <SettlementMobileCard
                          key={s.id}
                          settlement={s}
                          accountsById={accountsById}
                          advisories={advisories.data ?? []}
                          editable={editable && !readOnly}
                          onEdited={() => {
                            void qc.invalidateQueries({ queryKey: ['batch', batchId] });
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {(settlements.data?.length ?? 0) === 0 && (
              <p className="mt-3 text-caption text-ink-muted">
                No settlements yet. Import raw rows, then compute.
              </p>
            )}

            {/* Action buttons — hidden for read-only users */}
            {!readOnly && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {batch.data.state !== 'archived' && (
                  <Btn onClick={() => computeMut.mutate()} loading={computeMut.isPending}>
                    Compute / recompute
                  </Btn>
                )}
                {batch.data.state === 'draft' && (
                  <Btn
                    onClick={() => {
                      if (warns.length > 0) {
                        setVerifyModalOpen(true);
                      } else {
                        verifyMut.mutate(undefined);
                      }
                    }}
                    loading={verifyMut.isPending && !verifyModalOpen}
                    disabled={blocks.length > 0}
                  >
                    Verify batch
                  </Btn>
                )}
                {batch.data.state === 'verified' && (
                  <>
                    <Btn
                      onClick={() => {
                        const settlementsCount = settlements.data?.length ?? 0;
                        const ok = confirm(
                          `Post this batch to ledgers?\n\n` +
                            `${settlementsCount} settlement(s) will be written as immutable ledger entries ` +
                            `across client, upline, and self ledgers. Posted settlements cannot be edited ` +
                            `until the batch is unposted.\n\nContinue?`,
                        );
                        if (ok) postMut.mutate();
                      }}
                      loading={postMut.isPending}
                      disabled={blocks.length > 0}
                    >
                      Post to ledgers
                    </Btn>
                    <Btn
                      variant="secondary"
                      onClick={() => void withPin(() => unlockMut.mutateAsync())}
                      loading={unlockMut.isPending}
                    >
                      Unlock for correction
                    </Btn>
                  </>
                )}
                {batch.data.state === 'archived' && (
                  <>
                    <Btn onClick={() => setFixWizardOpen(true)}>
                      Reopen &amp; fix a mistake
                    </Btn>
                    <Btn
                      variant="danger"
                      onClick={() => {
                        const reason = prompt(
                          'Reason for unposting this batch?\n\n' +
                            'This will reverse every ledger entry created by this post. ' +
                            'Balances return to pre-post state.',
                        );
                        if (reason && reason.trim().length > 0) {
                          void withPin(() => unpostMut.mutateAsync(reason.trim()));
                        }
                      }}
                      loading={unpostMut.isPending}
                    >
                      Unpost batch
                    </Btn>
                  </>
                )}
                {batch.data.state !== 'archived' && (
                  <Btn
                    variant="danger"
                    onClick={() => {
                      const ok = confirm(
                        `Delete this batch?\n\nAll raw rows, settlements, and attachments will be permanently removed. This cannot be undone.`,
                      );
                      if (ok) deleteMut.mutate();
                    }}
                    loading={deleteMut.isPending}
                  >
                    Delete batch
                  </Btn>
                )}
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── Side cards (below the table on desktop, as a row) ───────────── */}
        <aside className="hidden min-w-0 gap-4 lg:grid lg:grid-cols-3">
          <div className="order-4 lg:order-1">
            <BatchNotesCard
              batchId={batchId}
              initialNotes={batch.data.notes}
              readOnly={readOnly}
              onError={(msg) => setGenericError(msg)}
          />
          </div>

          {/* Summary */}
          <div className="order-1 lg:order-2">
            <SectionCard section="batches" title="Summary">
            {totals.data ? (
              <dl className="space-y-1.5 text-small">
                <DRow label="State">
                  <StatePill state={batch.data.state} />
                </DRow>
                <DRow label="Rows">{totals.data.rowCount}</DRow>
                <DRow label="Upline cost">
                  <span className="font-mono text-ink">{totals.data.uplineCostSum}</span>
                </DRow>
                <DRow label="Client amount">
                  <span className="font-mono text-ink">{totals.data.clientAmountSum}</span>
                </DRow>
                <DRow label="Your margin">
                  <span className="font-mono font-semibold text-pos">{totals.data.userMarginSum}</span>
                </DRow>
                <DRow label="MAJOR variances">
                  <span className={totals.data.majorVarianceCount > 0 ? 'text-neg font-semibold' : 'text-ink'}>
                    {totals.data.majorVarianceCount}
                  </span>
                </DRow>
                <DRow label="Pending">{totals.data.pendingCount}</DRow>
              </dl>
            ) : (
              <p className="text-caption text-ink-muted">No totals yet.</p>
            )}
          </SectionCard>
          </div>

          {/* Attachments */}
          <div className="order-2 lg:order-3">
            <SectionCard section="batches" title="Attachments">
            {attachments.data && attachments.data.length > 0 ? (
              <ul className="space-y-2">
                {attachments.data.map((a) => (
                  <li key={a.id} className="rounded-md border border-border p-2.5">
                    <div className="flex items-center justify-between gap-1">
                      <span className="truncate text-small font-medium text-ink">
                        {a.originalFilename}
                      </span>
                      <span className="shrink-0 text-caption text-ink-muted">
                        {(a.byteSize / 1024).toFixed(0)} kB
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="font-mono text-caption text-ink-muted">
                        {a.sha256.slice(0, 12)}…
                      </span>
                      <a
                        href={`/api/attachments/${a.sha256}${attachmentExt(a.originalFilename)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-caption font-medium text-sec-batches hover:underline"
                      >
                        {isImageMimeType(a.mimeType) ? 'View' : 'Download'}
                      </a>
                    </div>
                    {features.data?.ocrEnabled &&
                      isImageMimeType(a.mimeType) &&
                      editable &&
                      !readOnly && (
                        <div className="mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2 py-1 text-small"
                            onClick={() => {
                              setGenericError(null);
                              ocrClassifyMut.mutate(a.id);
                            }}
                            disabled={ocrClassifyMut.isPending}
                          >
                            {ocrClassifyMut.isPending &&
                            ocrClassifyMut.variables === a.id ? (
                              <Spinner />
                            ) : (
                              'Extract from image'
                            )}
                          </Button>
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-caption text-ink-muted">No attachments yet.</p>
            )}

            {/* Attachments are metadata — always allow adding, even on posted batches */}
            {!readOnly && (
              <div className="mt-3 border-t border-border pt-3">
                <AttachmentUploader
                  batchId={batchId}
                  onUploaded={() =>
                    void qc.invalidateQueries({ queryKey: ['batch', batchId, 'attachments'] })
                  }
                  onError={(msg) => setGenericError(msg)}
                />
              </div>
            )}

            {editable && !readOnly && (
              <div className="mt-3 border-t border-border pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center"
                  onClick={() => setManualEntryOpen((o) => !o)}
                >
                  {manualEntryOpen ? 'Close manual entry ↑' : 'Manual entry ↓'}
                </Button>
              </div>
            )}
          </SectionCard>
          </div>

          {/* Raw rows */}
          <div className="order-3 lg:order-4">
            <SectionCard section="batches" title="Raw rows imported">
            <p className="text-small text-ink-muted">
              {rawRows.data?.length ?? 0} row(s).
              {(rawRows.data ?? []).filter((r) => r.matchedAccountId === null && !r.ignored).length > 0 && (
                <span className="ml-1 font-medium text-warn">
                  {(rawRows.data ?? []).filter((r) => r.matchedAccountId === null && !r.ignored).length}{' '}
                  unmatched
                </span>
              )}
            </p>
            </SectionCard>
          </div>
        </aside>
      </div>

      {editable && !readOnly && manualEntryOpen && batch.data && (
        <Modal onClose={() => setManualEntryOpen(false)} size="lg">
          <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
            <h2 className="text-small font-semibold text-ink">Manual entry</h2>
            <Button variant="ghost" onClick={() => setManualEntryOpen(false)}>Close</Button>
          </header>
          <div className="flex-1 overflow-y-auto p-4">
            <ManualEntryGrid
              batchId={batchId}
              uplinePartyId={batch.data.uplinePartyId}
              weekMonday={batch.data.weekMonday}
              onClose={() => setManualEntryOpen(false)}
              onSaved={(count) => {
                setManualEntryOpen(false);
                setGenericError(null);
                void qc.invalidateQueries({ queryKey: ['batch', batchId] });
                const prev = document.title;
                document.title = `✓ Saved ${count} row(s)`;
                setTimeout(() => { document.title = prev; }, 3000);
              }}
            />
          </div>
        </Modal>
      )}

      {ocrAliasData && (
        <OcrAliasStep
          data={ocrAliasData}
          onCancel={() => setOcrAliasData(null)}
          onConfirmed={(allowed, workbookData) => {
            setOcrAliasData(null);
            setAllowedAccountNames(allowed);
            setOcrPreseeded(workbookData);
            setImportOpen(true);
          }}
        />
      )}

      {importOpen && (
        <ImportDialog
          batchId={batchId}
          preSeeded={ocrPreseeded ?? undefined}
          allowedAccountNames={allowedAccountNames}
          onClose={() => {
            setImportOpen(false);
            setOcrPreseeded(null);
            setAllowedAccountNames(undefined);
          }}
          onImported={() => {
            setImportOpen(false);
            setOcrPreseeded(null);
            setAllowedAccountNames(undefined);
            void qc.invalidateQueries({ queryKey: ['batch', batchId] });
          }}
        />
      )}

      {verifyModalOpen && (
        <VerifyConfirmationModal
          warnings={warns}
          busy={verifyMut.isPending}
          onCancel={() => setVerifyModalOpen(false)}
          onConfirm={() => verifyMut.mutate(warns)}
        />
      )}

      {fixWizardOpen && !readOnly && batch.data && (
        <ReopenFixWizard
          batch={batch.data}
          onClose={() => {
            setFixWizardOpen(false);
            void qc.invalidateQueries({ queryKey: ['batch', batchId] });
            void qc.invalidateQueries({ queryKey: ['rate-drift'] });
          }}
        />
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function ClientGroupRows({
  clientKey,
  rows,
  accountsById,
  clientsById,
  advisories,
  editable,
  onEdited,
}: {
  clientKey: string;
  rows: Settlement[];
  accountsById: Map<number, Account>;
  clientsById: Map<number, Party>;
  advisories: SettlementAdvisory[];
  editable: boolean;
  onEdited: () => void;
}) {
  const clientName =
    clientKey === 'self'
      ? 'Self (company)'
      : (clientsById.get(Number(clientKey))?.displayName ?? `Client #${clientKey}`);

  // Per-client subtotals
  let uplineCostSum = 0;
  let clientAmountSum = 0;
  let marginSum = 0;
  for (const s of rows) {
    uplineCostSum += Number(s.uplineCost ?? 0);
    clientAmountSum += Number(s.clientAmount ?? 0);
    marginSum += Number(s.userMargin ?? 0);
  }

  return (
    <>
      {/* Client section header */}
      <tr className="bg-sec-batches-tint">
        <td
          colSpan={10}
          className="px-4 py-2.5 text-caption font-semibold uppercase tracking-wide text-sec-batches"
        >
          {clientName}
          <span className="ml-2 font-normal normal-case tracking-normal text-sec-batches opacity-70">
            ({rows.length} account{rows.length !== 1 ? 's' : ''})
          </span>
        </td>
        <td className="sticky right-0 bg-sec-batches-tint" />
      </tr>
      {rows.map((s) => (
        <SettlementRow
          key={s.id}
          settlement={s}
          accountsById={accountsById}
          clientsById={clientsById}
          advisories={advisories}
          editable={editable}
          onEdited={onEdited}
        />
      ))}
      {/* Per-client subtotal */}
      <tr className="border-t border-border bg-surface-raised text-caption font-semibold">
        <td className="px-4 py-2.5 italic text-ink-muted">{clientName} total</td>
        {/* client side: pts, rate, amt */}
        <td className="border-l border-border/50 px-4 py-2.5" />
        <td className="px-4 py-2.5" />
        <td className="px-4 py-2.5 text-right font-mono text-sec-batches">
          {clientAmountSum.toFixed(3)}
        </td>
        {/* upline side: pts, rate, cost */}
        <td className="border-l border-border/50 px-4 py-2.5" />
        <td className="px-4 py-2.5" />
        <td className="px-4 py-2.5 text-right font-mono text-brand">
          {uplineCostSum.toFixed(3)}
        </td>
        {/* margin */}
        <td className={`border-l border-border/50 px-4 py-2.5 text-right font-mono ${marginSum >= 0 ? 'text-pos' : 'text-neg'}`}>
          {marginSum.toFixed(3)}
        </td>
        <td colSpan={2} />
        <td className="sticky right-0 bg-surface-raised shadow-[-10px_0_12px_-10px_rgba(15,23,42,0.18)]" />
      </tr>
    </>
  );
}

function DRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

function SettlementMobileCard({
  settlement,
  accountsById,
  advisories,
  editable,
  onEdited,
}: {
  settlement: Settlement;
  accountsById: Map<number, Account>;
  advisories: SettlementAdvisory[];
  editable: boolean;
  onEdited: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [uplinePoints, setUplinePoints] = useState(settlement.uplinePoints);
  const [clientPoints, setClientPoints] = useState(settlement.clientPoints);
  const [err, setErr] = useState<string | null>(null);

  // Identical endpoints + request body as the desktop SettlementRow — the
  // server owns all the math, so points post byte-identically either way.
  const saveMut = useMutation({
    mutationFn: () =>
      api.put<Settlement>(`/api/settlements/${settlement.id}/points-override`, {
        uplinePoints,
        clientPoints,
      }),
    onSuccess: () => {
      setErr(null);
      setEditing(false);
      void qc.invalidateQueries();
      onEdited();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  const clearMut = useMutation({
    mutationFn: () =>
      api.delete<Settlement>(`/api/settlements/${settlement.id}/points-override`),
    onSuccess: () => {
      void qc.invalidateQueries();
      onEdited();
    },
  });

  const accountName =
    accountsById.get(settlement.accountId)?.canonicalName ?? `#${settlement.accountId}`;
  const margin = Number(settlement.userMargin ?? 0);
  const myAdvisories = advisories.filter((a) => a.settlementId === settlement.id);

  return (
    <div
      data-row-kind="settlement"
      data-row-id={settlement.id}
      className="overflow-hidden rounded-[22px] border border-border/70 app-shell-surface p-4 shadow-sm backdrop-blur"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-small font-semibold tracking-[-0.01em] text-ink">
            {accountName}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatePill state={settlement.state} />
            {settlement.varianceSeverity !== 'NONE' && (
              <StatePill state={settlement.varianceSeverity.toLowerCase()} />
            )}
            {settlement.pointsOverridden && (
              <span className="rounded bg-sec-batches-tint px-1.5 py-0.5 text-[10px] text-sec-batches">
                overridden
              </span>
            )}
          </div>
        </div>
        <div className={margin >= 0 ? 'text-pos' : 'text-neg'}>
          <div className="text-[11px] font-medium uppercase tracking-[0.12em]">Margin</div>
          <div className="mt-1 font-mono text-small font-semibold">{settlement.userMargin ?? '—'}</div>
        </div>
      </div>

      {myAdvisories.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {myAdvisories.map((a, i) => (
            <span
              key={i}
              title={a.detail}
              className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
            >
              {a.kind.replace('_', ' ')}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <SettlementMetric label="Client amt" value={settlement.clientAmount ?? '—'} />
        <SettlementMetric label="Upline cost" value={settlement.uplineCost ?? '—'} />
        {editing ? (
          <>
            <div className="rounded-[16px] bg-surface-raised/90 px-3 py-2.5 ring-1 ring-border/50">
              <div className="text-[10px] font-medium text-ink-muted">Client pts</div>
              <input
                className="mt-1 min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-right font-mono text-base text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                value={clientPoints}
                inputMode="decimal"
                onChange={(e) => setClientPoints(e.target.value)}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="rounded-[16px] bg-surface-raised/90 px-3 py-2.5 ring-1 ring-border/50">
              <div className="text-[10px] font-medium text-ink-muted">Upline pts</div>
              <input
                className="mt-1 min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-right font-mono text-base text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                value={uplinePoints}
                inputMode="decimal"
                onChange={(e) => setUplinePoints(e.target.value)}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </>
        ) : (
          <>
            <SettlementMetric label="Client pts" value={settlement.clientPoints ?? '—'} />
            <SettlementMetric label="Upline pts" value={settlement.uplinePoints ?? '—'} />
          </>
        )}
      </div>

      {err && (
        <div className="mt-3">
          <ErrorBanner>{err}</ErrorBanner>
        </div>
      )}

      {editable && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {editing ? (
            <>
              <Button
                variant="ghost"
                className="min-h-[44px] w-full"
                onClick={() => {
                  setEditing(false);
                  setErr(null);
                  setUplinePoints(settlement.uplinePoints);
                  setClientPoints(settlement.clientPoints);
                }}
              >
                Cancel
              </Button>
              <Button
                className="min-h-[44px] w-full"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate()}
              >
                {saveMut.isPending ? <Spinner /> : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="min-h-[44px] w-full" onClick={() => setEditing(true)}>
                Edit
              </Button>
              {settlement.pointsOverridden && (
                <Button
                  variant="ghost"
                  className="min-h-[44px] w-full"
                  disabled={clearMut.isPending}
                  onClick={() => clearMut.mutate()}
                >
                  Clear override
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {settlement.notes && (
        <div className="mt-3 border-t border-border/70 pt-3 text-[12px] leading-5 text-ink-muted">
          {settlement.notes}
        </div>
      )}
    </div>
  );
}

function SettlementMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[16px] bg-surface-raised/90 px-3 py-2.5 ring-1 ring-border/50">
      <div className="text-[10px] font-medium text-ink-muted">{label}</div>
      <div className="mt-1 font-mono text-small font-semibold text-ink">{value}</div>
    </div>
  );
}
function isImageMimeType(mimeType: string): boolean {
  return mimeType === 'image/jpeg' || mimeType === 'image/png';
}

function attachmentExt(filename: string): string {
  const m = /(\.[a-zA-Z0-9]{1,10})$/.exec(filename);
  return m?.[1]?.toLowerCase() ?? '';
}

/**
 * Uploader for additional attachments on an already-open batch.
 */
function AttachmentUploader({
  batchId,
  onUploaded,
  onError,
}: {
  batchId: number;
  onUploaded: () => void;
  onError: (msg: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files: File[]) => {
    if (files.length === 0) return;
    setBusy(true);
    try {
      for (const f of files) {
        const base64 = await fileToBase64(f);
        await api.post<unknown>(`/api/batches/${batchId}/attachments`, {
          base64,
          originalFilename: f.name,
          mimeType: f.type || 'application/octet-stream',
        });
      }
      onUploaded();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,.xlsx"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          void upload(files);
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-center"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? <Spinner /> : '+ Add attachment'}
      </Button>
    </>
  );
}

function fileToBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(f);
  });
}

/**
 * Inline-editable batch notes. Saves on blur.
 */
function BatchNotesCard({
  batchId,
  initialNotes,
  readOnly,
  onError,
}: {
  batchId: number;
  initialNotes: string | null;
  readOnly: boolean;
  onError: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(initialNotes ?? '');
  const [showSaved, setShowSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveMut = useMutation({
    mutationFn: (notes: string | null) =>
      api.patch<WeeklyBatch>(`/api/batches/${batchId}`, { notes }),
    onMutate: () => setSaving(true),
    onSuccess: () => {
      setSaving(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1500);
      void qc.invalidateQueries({ queryKey: ['batch', batchId] });
    },
    onError: (e) => {
      setSaving(false);
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onBlur = () => {
    if (readOnly) return;
    const next = draft.trim().length === 0 ? null : draft;
    const prev = initialNotes ?? '';
    if ((next ?? '') === prev) return; // no change
    saveMut.mutate(next);
  };

  return (
    <SectionCard
      section="batches"
      title="Notes"
      action={
        saving ? (
          <span className="text-caption text-ink-muted">saving…</span>
        ) : showSaved ? (
          <span className="text-caption text-pos">saved ✓</span>
        ) : null
      }
    >
      <textarea
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={onBlur}
        readOnly={readOnly}
        placeholder="e.g. SRB changed rates Wednesday; double-check row 7"
        className="w-full resize-y rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 read-only:cursor-default read-only:bg-surface-raised"
      />
      <p className="mt-1 text-caption text-ink-muted">
        Metadata only — doesn&apos;t affect amounts or balances.
      </p>
    </SectionCard>
  );
}





